import { useCallback, useState } from "react"
import PropTypes from "prop-types"
import { Button, ListItemText, ListSubheader, Menu, MenuItem, Typography } from "@mui/material"
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material"
import OPTooltip from "components/material/tooltip"
import RouterAnchor from "components/router-anchor"
import ResourceName from "components/resource-name"
import { PORTFOLIO } from "../../shapes"
import "./style.scss"

const MENU_ID = "portfolio-selector-menu"

// MUI Buttons uppercase their label; this one holds a name the user typed, so it renders as written.
// minWidth lets the label shrink below its content, which is what the ellipsis needs in the flex row.
const TOGGLE_SX = (theme) => ({
  textTransform: "none",
  maxWidth: "20rem",
  // Matches the round icon buttons beside it: MUI tints a text Button with the primary colour but
  // an IconButton with the neutral action one. Same expression IconButton uses, `theme.vars` guard
  // included — that is absent on a theme built without `cssVariables`, as in unit tests.
  "&:hover": {
    backgroundColor: theme.alpha(
      (theme.vars || theme).palette.action.active,
      (theme.vars || theme).palette.action.hoverOpacity,
    ),
  },
  "& .toggle-name": {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
})

// The model sits on the first line beside the name, so the row aligns to the top rather than
// centring against a two-line block. MenuItem is nowrap by default, which clipped the old row.
const ENTRY_SX = { whiteSpace: "normal", alignItems: "flex-start", gap: 2 }

// Both lines stay single so the list keeps a scannable rhythm; names and descriptions both run long.
// minWidth lets this shrink below its content, which is what the ellipsis needs in the flex row.
const ENTRY_TEXT_SX = {
  my: 0,
  minWidth: 0,
  // The row is a link, so the name keeps the app's link colour — same token the global `a` rule
  // uses, set here rather than inherited so wrapping the text in a Typography can't drop it.
  "& .MuiListItemText-primary": { color: "var(--opcolor-blue-light)" },
  "& .MuiListItemText-primary, & .MuiListItemText-secondary": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}

// Never shrinks — the name gives up width first, since the model is the shorter of the two.
const MODEL_SX = { flexShrink: 0, color: "text.secondary", lineHeight: "1.5rem" }

const MENU_SLOT_PROPS = { paper: { sx: { maxWidth: "28rem" } } }

function RecentPortfolio({ portfolio, onSelect }) {
  return <MenuItem component={RouterAnchor} to={portfolio.to} sx={ENTRY_SX} onClick={onSelect}>
    <ListItemText
      primary={portfolio.name}
      secondary={portfolio.description || undefined}
      sx={ENTRY_TEXT_SX}
    />
    <OPTooltip title={portfolio.modelName} placement="right">
      <Typography variant="caption" sx={MODEL_SX}>{portfolio.model}</Typography>
    </OPTooltip>
  </MenuItem>
}

RecentPortfolio.propTypes = {
  portfolio: PORTFOLIO.isRequired,
  onSelect: PropTypes.func.isRequired,
}

export default function PortfolioSelector({ portfolioName, isLoading, latestPortfolios, trailingActions }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const closeMenu = useCallback(() => { setAnchorEl(null) }, [])

  return <div className="portfolio-selector">
    <Button
      className="current-name"
      aria-haspopup="true"
      aria-expanded={open}
      aria-controls={open ? MENU_ID : undefined}
      onClick={(event) => setAnchorEl(event.currentTarget)}
      endIcon={open ? <ArrowDropUp /> : <ArrowDropDown />}
      sx={TOGGLE_SX}
    >
      <span className="toggle-name" title={portfolioName}>
        <ResourceName name={portfolioName} isLoading={isLoading} />
      </span>
    </Button>
    {trailingActions}
    <Menu id={MENU_ID} anchorEl={anchorEl} open={open} onClose={closeMenu} slotProps={MENU_SLOT_PROPS}>
      <ListSubheader>Recently viewed</ListSubheader>
      {latestPortfolios.map(portfolio =>
        <RecentPortfolio key={portfolio.id} portfolio={portfolio} onSelect={closeMenu} />,
      )}
    </Menu>
  </div>
}

PortfolioSelector.propTypes = {
  portfolioName: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  latestPortfolios: PropTypes.arrayOf(PORTFOLIO).isRequired,
  // Controls rendered after the name, inside the selector but outside its toggle (e.g. the ⋮ menu).
  trailingActions: PropTypes.node,
}
